import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  ArrowLeftIcon,
  CheckIcon,
  ClockIcon,
  LinkIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Chip, Loader } from "~/ui-rtk/components/ui/common"
import { Button } from "~/ui-rtk/components/ui/controls"
import { Toggle } from "~/ui-rtk/components/ui/controls/Toggle"
import {
  useArchiveMegaFeedStructureMutation,
  useGetMegaFeedStructureFeedUrlMutation,
  useGetMegaFeedStructureVocabularyQuery,
  useListMegaFeedStructuresQuery,
  useStartMegaFeedStructureRunMutation,
  type MegaFeedSlotKindOption,
  type MegaFeedStructure,
} from "~/ui-rtk/api/megaFeedStructureApi"
import { absoluteFeedUrl } from "../feedUrl"
import StructureEditor from "./StructureEditor"
import StructureRunHistory from "./StructureRunHistory"
import StructureRunView from "./StructureRunView"

/** The API's per-run cap. */
const MAX_REFS = 500

export interface StructuredFeedProduct {
  productId: string
  title: string
  /** The product's colorways; empty when it has no color option. */
  colors: string[]
}

type View =
  | { name: "list" }
  | { name: "edit"; structure?: MegaFeedStructure }
  | { name: "runs"; structure: MegaFeedStructure }
  | { name: "run"; runId: string; structure: MegaFeedStructure }

/** What a structure card is waiting on, so that button shows a spinner. */
type Busy = "link" | "archive" | "apply" | null

/** How long the copy button shows its check after a copy. */
const COPIED_MS = 1500

type Props = {
  isOpen: boolean
  onClose: () => void
  /** The products on the current Mega Feed page: what "Apply" targets. */
  products: StructuredFeedProduct[]
}

function StructureCard({
  structure,
  vocabulary,
  canApply,
  busy,
  isCopied,
  onEdit,
  onHistory,
  onCopyFeedLink,
  onArchive,
  onApply,
}: {
  structure: MegaFeedStructure
  vocabulary: MegaFeedSlotKindOption[]
  canApply: boolean
  busy: Busy
  isCopied: boolean
  onEdit: () => void
  onHistory: () => void
  onCopyFeedLink: () => void
  onArchive: () => void
  onApply: () => void
}) {
  const slotName = (slot: MegaFeedStructure["slots"][number]) => {
    if (slot.label) return slot.label
    const option = vocabulary.find(o => o.kind === slot.kind)
    const criterion = option?.criteria.find(c => c.value === slot.criterion)
    return criterion
      ? `${option?.label}: ${criterion.label}`
      : (option?.label ?? slot.kind)
  }

  return (
    <div className="bg-system-blue-800 rounded-lg p-3 flex items-start gap-system-sm">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-basic-white">
          {structure.name}
        </div>
        {structure.description && (
          <div className="text-xs text-cadet-blue mt-0.5">
            {structure.description}
          </div>
        )}
        <div className="flex flex-wrap gap-1 mt-2">
          {structure.slots.map((slot, index) => (
            <Chip
              key={slot.id}
              className="bg-system-blue-700 text-basic-white text-xs"
            >
              {index + 1}. {slot.mediaType === "VIDEO" ? "🎬 " : ""}
              {slotName(slot)}
            </Chip>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-system-xs shrink-0">
        <button
          type="button"
          aria-label="Edit structure"
          onClick={onEdit}
          className="text-cadet-blue hover:text-basic-white p-1"
        >
          <PencilSquareIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          aria-label="Run history"
          title="Past runs and their feeds"
          onClick={onHistory}
          className="text-cadet-blue hover:text-basic-white p-1"
        >
          <ClockIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          aria-label="Copy feed link"
          title="Copy this structure's Meta supplemental-feed URL"
          onClick={onCopyFeedLink}
          disabled={busy === "link"}
          className="flex text-cadet-blue hover:text-basic-white p-1 disabled:cursor-wait"
        >
          {busy === "link" && (
            <span className="loading loading-spinner w-4 h-4 text-basic-white" />
          )}
          {busy !== "link" && isCopied && (
            <CheckIcon className="w-4 h-4 text-green-400" />
          )}
          {busy !== "link" && !isCopied && <LinkIcon className="w-4 h-4" />}
        </button>
        <button
          type="button"
          aria-label="Archive structure"
          onClick={onArchive}
          disabled={busy === "archive"}
          className="flex text-cadet-blue hover:text-red-400 p-1 disabled:cursor-wait"
        >
          {busy === "archive" ? (
            <span className="loading loading-spinner w-4 h-4 text-basic-white" />
          ) : (
            <TrashIcon className="w-4 h-4" />
          )}
        </button>
        <Button
          size="sm"
          onClick={onApply}
          disabled={!canApply}
          isLoading={busy === "apply"}
        >
          Apply
        </Button>
      </div>
    </div>
  )
}

/**
 * Structured feed (MD-5679): define an ordered list of image / video slots
 * once, then fill them for many products and colorways in one run.
 */
export default function StructuredFeedDialog({
  isOpen,
  onClose,
  products,
}: Props) {
  const [view, setView] = useState<View>({ name: "list" })
  const [busy, setBusy] = useState<{ id: string; action: Busy } | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  useEffect(() => {
    if (!copiedId) return undefined
    const timer = setTimeout(() => setCopiedId(null), COPIED_MS)
    return () => clearTimeout(timer)
  }, [copiedId])
  const [perColorway, setPerColorway] = useState(true)

  const { data: vocabulary = [] } = useGetMegaFeedStructureVocabularyQuery(
    undefined,
    { skip: !isOpen },
  )
  const { data: structures = [], isLoading } = useListMegaFeedStructuresQuery(
    undefined,
    { skip: !isOpen },
  )
  const [archiveStructure] = useArchiveMegaFeedStructureMutation()
  const [getFeedUrl] = useGetMegaFeedStructureFeedUrlMutation()
  const [startRun, { isLoading: isStarting }] =
    useStartMegaFeedStructureRunMutation()

  const hasColors = products.some(p => p.colors.length > 0)
  // A color ref `<productId>_<color>` gets its own media (every size of the
  // color shares it); a product without colors is targeted as itself.
  const { refs, refLabels } = useMemo(() => {
    // An array keeps page order: a plain object would hoist numeric-looking
    // product ids ahead of color refs.
    const entries: [string, string][] = products.flatMap(product =>
      perColorway && product.colors.length > 0
        ? product.colors.map(
            color =>
              [
                `${product.productId}_${color}`,
                `${product.title} · ${color}`,
              ] as [string, string],
          )
        : [[product.productId, product.title] as [string, string]],
    )
    const unique = [...new Map(entries).entries()]
    return {
      refs: unique.map(([ref]) => ref),
      refLabels: Object.fromEntries(unique),
    }
  }, [products, perColorway])

  const close = () => {
    setView({ name: "list" })
    onClose()
  }

  const apply = async (structure: MegaFeedStructure) => {
    setBusy({ id: structure.id, action: "apply" })
    try {
      const run = await startRun({
        structureId: structure.id,
        productRefIds: refs.slice(0, MAX_REFS),
      }).unwrap()
      setView({ name: "run", runId: run.id, structure })
    } catch {
      toast.error("Couldn't start the run, try again.")
    } finally {
      setBusy(null)
    }
  }

  const copyFeedLink = async (structure: MegaFeedStructure) => {
    setBusy({ id: structure.id, action: "link" })
    try {
      const { url } = await getFeedUrl(structure.id).unwrap()
      await navigator.clipboard.writeText(absoluteFeedUrl(url))
      setCopiedId(structure.id)
      toast.success(
        `"${structure.name}" feed link copied — paste it into Meta Commerce Manager as a supplementary data source. It serves the last run you publish.`,
      )
    } catch {
      toast.error("Couldn't get the feed link — try again.")
    } finally {
      setBusy(null)
    }
  }

  const archive = async (structure: MegaFeedStructure) => {
    if (
      !window.confirm(`Archive "${structure.name}"? Past runs stay readable.`)
    ) {
      return
    }
    setBusy({ id: structure.id, action: "archive" })
    try {
      await archiveStructure(structure.id).unwrap()
    } catch {
      toast.error("Couldn't archive the structure.")
    } finally {
      setBusy(null)
    }
  }

  const titleByView: Record<View["name"], string> = {
    list: "Structured feed",
    edit:
      view.name === "edit" && view.structure
        ? `Edit ${view.structure.name}`
        : "New structure",
    runs: view.name === "runs" ? `${view.structure.name}: history` : "",
    run: view.name === "run" ? `${view.structure.name}: run` : "",
  }
  const title = titleByView[view.name]
  // A run goes back to its structure's history, the history to the list.
  let back: (() => void) | null = null
  if (view.name === "runs") back = () => setView({ name: "list" })
  if (view.name === "run") {
    const { structure } = view
    back = () => setView({ name: "runs", structure })
  }

  return (
    // Closes on the X, Escape or a click outside. The library picker opened
    // from the editor is its own portal, so clicks in it don't close this.
    <Dialog isOpen={isOpen} handleClose={close} closeOnClickOutside width={960}>
      <div className="bg-basic-dark-blue border border-basic-blue rounded-lg p-6 text-left">
        <div className="flex items-start justify-between gap-system-sm mb-1">
          <div className="flex items-center gap-system-xs min-w-0">
            {back && (
              <button
                type="button"
                aria-label="Back"
                onClick={back}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-cadet-blue hover:bg-white/10 hover:text-basic-white"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
            )}
            <h3 className="text-lg font-semibold text-basic-white">{title}</h3>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={close}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-cadet-blue hover:bg-white/10 hover:text-basic-white"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        {view.name === "list" && (
          <p className="text-sm text-cadet-blue mb-4">
            A structure is the shot list every product&apos;s feed media
            follows. Applying it picks the matching asset for each slot and
            generates a missing colorway shot from a sibling color. Each
            structure has its own feed link; publishing a run puts it there.
          </p>
        )}

        {view.name === "edit" && (
          <StructureEditor
            vocabulary={vocabulary}
            structure={view.structure}
            onSaved={() => setView({ name: "list" })}
            onCancel={() => setView({ name: "list" })}
          />
        )}

        {view.name === "runs" && (
          <StructureRunHistory
            structureId={view.structure.id}
            onOpen={run =>
              setView({ name: "run", runId: run.id, structure: view.structure })
            }
          />
        )}

        {view.name === "run" && (
          <StructureRunView
            runId={view.runId}
            structure={view.structure}
            refLabels={refLabels}
            onDone={close}
          />
        )}

        {view.name === "list" && (
          <div className="flex flex-col gap-system-sm">
            <div className="flex items-center justify-between gap-system-sm flex-wrap">
              <div className="flex items-center gap-system-sm text-sm text-basic-white">
                <span>
                  Applies to the {products.length} products on this page
                  {hasColors && perColorway ? `, ${refs.length} colorways` : ""}
                  {refs.length > MAX_REFS ? ` (first ${MAX_REFS})` : ""}
                </span>
                {hasColors && (
                  <label className="flex items-center gap-system-xs text-xs">
                    <Toggle
                      size="xs"
                      value={perColorway}
                      onChange={setPerColorway}
                    />
                    Per colorway
                  </label>
                )}
              </div>
              <Button size="sm" onClick={() => setView({ name: "edit" })}>
                + New structure
              </Button>
            </div>

            {isLoading && (
              <div className="flex justify-center p-6">
                <Loader />
              </div>
            )}
            {!isLoading && structures.length === 0 && (
              <div className="text-sm text-cadet-blue p-6 text-center border border-dashed border-basic-blue rounded-lg">
                No structures yet. Create one with the shots each product should
                carry, e.g. 1 hero, 2 lifestyle, 3 detail, 4 top performer.
              </div>
            )}
            <div className="flex flex-col gap-system-xs max-h-[55vh] overflow-y-auto">
              {structures.map(structure => (
                <StructureCard
                  key={structure.id}
                  structure={structure}
                  vocabulary={vocabulary}
                  canApply={refs.length > 0 && !isStarting}
                  busy={busy?.id === structure.id ? busy.action : null}
                  isCopied={copiedId === structure.id}
                  onEdit={() => setView({ name: "edit", structure })}
                  onHistory={() => setView({ name: "runs", structure })}
                  onCopyFeedLink={() => void copyFeedLink(structure)}
                  onArchive={() => void archive(structure)}
                  onApply={() => void apply(structure)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  )
}
